import { useEffect, useState } from 'react'
import { api } from '../lib/api'

const Dashboard = () => {
  const [stats, setStats] = useState(null)
  const [activity, setActivity] = useState([])
  const [error, setError] = useState('')

  useEffect(() => {
    const load = async () => {
      try {
        const [statsResponse, deployments] = await Promise.all([
          api.getStats(),
          api.getDeployments(),
        ])

        setStats(statsResponse)
        setActivity(
          deployments.slice(0, 3).map((item) => ({
            title: item.projectId || 'project',
            status: item.status,
            detail: item.createdAt
              ? `Deployed ${new Date(item.createdAt).toLocaleString()}`
              : 'Recent deploy',
          }))
        )
      } catch (err) {
        setError(err.message || 'Failed to load dashboard')
      }
    }

    load()
  }, [])

  return (
    <div className="space-y-10">
      <section>
        <div className="flex flex-col gap-2">
          <div className="text-xs uppercase tracking-widest text-slate-500">Overview</div>
          <h1 className="text-3xl font-semibold">Mission control</h1>
          <p className="text-slate-600">Keep an eye on performance, deployments, and usage in one place.</p>
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-4">
        <div className="rounded-2xl border border-slate-200 bg-white/85 p-5 shadow-sm">
          <div className="text-xs uppercase tracking-widest text-slate-500">Total projects</div>
          <div className="mt-3 text-2xl font-semibold">{stats?.projectCount ?? '--'}</div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white/85 p-5 shadow-sm">
          <div className="text-xs uppercase tracking-widest text-slate-500">Deployments</div>
          <div className="mt-3 text-2xl font-semibold">{stats?.deploymentCount ?? '--'}</div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white/85 p-5 shadow-sm">
          <div className="text-xs uppercase tracking-widest text-slate-500">Plan</div>
          <div className="mt-3 text-2xl font-semibold">{stats?.plan ?? '--'}</div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white/85 p-5 shadow-sm">
          <div className="text-xs uppercase tracking-widest text-slate-500">Max projects</div>
          <div className="mt-3 text-2xl font-semibold">{stats?.limits?.maxProjects ?? '--'}</div>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-[2fr_1fr]">
        <div className="rounded-3xl border border-slate-200 bg-white/85 p-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500">Deployments</div>
              <h2 className="text-xl font-semibold">Recent activity</h2>
            </div>
            <button className="rounded-full border border-slate-300 px-4 py-2 text-xs font-semibold text-ink">
              View all
            </button>
          </div>
          <div className="mt-6 space-y-4">
            {error ? (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-xs text-rose-600">
                {error}
              </div>
            ) : null}
            {activity.map((item) => (
              <div key={item.title} className="flex items-center justify-between rounded-2xl border border-slate-200 bg-slate-100 p-4">
                <div>
                  <div className="text-sm font-semibold">{item.title}</div>
                  <div className="text-xs text-slate-500">{item.detail}</div>
                </div>
                <div className="rounded-full bg-slate-200 px-3 py-1 text-xs font-semibold text-ink">
                  {item.status}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-3xl border border-slate-200 bg-white/85 p-6">
          <div className="text-xs uppercase tracking-widest text-slate-500">Insights</div>
          <h2 className="mt-2 text-xl font-semibold">System health</h2>
          <p className="mt-3 text-sm text-slate-600">
            Cloud Run services are stable. Build queue is below 10s in asia-south1.
          </p>
          <div className="mt-6 space-y-3 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-slate-500">Build queue</span>
              <span className="font-semibold text-ink">8s</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-500">Cache hit rate</span>
              <span className="font-semibold text-ink">78%</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-500">Cold starts</span>
              <span className="font-semibold text-ink">Low</span>
            </div>
          </div>
          <button className="mt-6 w-full rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white">
            Optimize deployments
          </button>
        </div>
      </section>
    </div>
  )
}

export default Dashboard
